import { Seo } from '../components/Seo';
import Logo from '../components/Logo';
import PublicNav from '../components/PublicNav';
import LazyVideo from '../components/LazyVideo';
import MobileCtaBar from '../components/MobileCtaBar';
import { Helmet } from 'react-helmet-async';
import { SOFTWARE_APPLICATION_SCHEMA, ORGANIZATION_SCHEMA } from '../lib/structuredData';
import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Camera, FileSpreadsheet, Shield, Sparkles, Eye, MapPin, Calendar, Receipt, CheckCircle, LayoutDashboard, Upload, Package, DollarSign, Lock, BellRing , ArrowRight} from 'lucide-react';

const Landing = () => {
  const { isAuthenticated, user } = useAuth();
  
  const features = [
    {
      icon: Eye,
      title: 'Forensic AI Analysis',
      description: "Our AI doesn't just list a 'TV' — it identifies the exact model, brand, and specifications with confidence scoring."
    },
    {
      icon: Calendar,
      title: 'Photo Metadata Verification',
      description: 'Automatically captures when and where each photo was taken, creating undisputable proof of ownership.'
    },
    {
      icon: Receipt,
      title: 'Digital Receipt Storage',
      description: 'Attach receipts and proof of purchase to each item for complete documentation.'
    },
    {
      icon: Shield,
      title: 'Bionic Inventory',
      description: 'Create an inventory so detailed and verifiable, it\'s virtually impossible to dispute at claim time.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="AI Home Inventory & Insurance Claim Evidence | VerifAEye"
        description="Turn your smartphone into a forensic insurance assessor. AI photo analysis, EXIF-backed proof and secure storage in your own Google Drive. Free to start."
        path="/"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(SOFTWARE_APPLICATION_SCHEMA)}</script>
        <script type="application/ld+json">{JSON.stringify(ORGANIZATION_SCHEMA)}</script>
      </Helmet>
      <PublicNav isAuthenticated={isAuthenticated} current="/" />
      <main id="main">

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8" style={{ background: 'linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <p className="text-blue-600 font-medium mb-4 tracking-wide">THE PROOF IS IN THE DETAIL</p>
              <h1 
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              >
                Stop Guessing,<br />
                <span className="text-blue-600">Start Proving.</span>
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-lg">
                VerifAEye turns your smartphone into a forensic insurance assessor. Create a 'Bionic Inventory' 
                that's virtually impossible to dispute.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                {isAuthenticated ? (
                  <>
                    <Link to="/upload">
                      <Button 
                        data-testid="hero-upload-btn"
                        className="bg-slate-900 text-white hover:bg-slate-800 px-8 py-6 text-base"
                      >
                        <Upload className="w-5 h-5 mr-2" />
                        Upload Photos
                      </Button>
                    </Link>
                    <Link to="/inventory">
                      <Button 
                        variant="outline"
                        data-testid="hero-inventory-btn"
                        className="border-slate-300 text-slate-700 hover:bg-slate-50 px-8 py-6 text-base"
                      >
                        <Package className="w-5 h-5 mr-2" />
                        View Inventory
                      </Button>
                    </Link>
                  </>
                ) : (
                  <>
                    <Link to="/register">
                      <Button 
                        data-testid="hero-get-started-btn"
                  style={{ minHeight: 48 }}
                        className="bg-slate-900 text-white hover:bg-slate-800 px-8 py-6 text-base"
                      >
                        Start Your Inventory
                      </Button>
                    </Link>
                    <Link to="/login">
                      <Button 
                        variant="outline"
                        data-testid="hero-signin-btn"
                        className="border-slate-300 text-slate-700 hover:bg-slate-50 px-8 py-6 text-base"
                      >
                        Sign In
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
            <div className="animate-slide-up stagger-2 flex items-center justify-center">
              <LazyVideo src="/videos/proof.mp4" testId="hero-proof-video" label="Play the proof walkthrough" className="w-full max-w-xl rounded-2xl shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 
                className="text-3xl font-semibold text-white tracking-tight mb-6"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              >
                Most people only realise their insurance coverage is a 'best guess' after disaster strikes.
              </h2>
              <p className="text-xl text-slate-300 leading-relaxed">
                VerifAEye removes the doubt. By combining high-resolution photo analysis with embedded metadata 
                and digital receipt storage, we create a 'Bionic Inventory' that is virtually impossible to dispute.
              </p>
            </div>
            <div className="flex items-center justify-center">
              <LazyVideo src="/videos/showcase.mp4" testId="showcase-video" label="Play the product showcase" className="w-full rounded-2xl shadow-xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing / Privacy / Notifications — What you get before you sign up */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50 border-y border-slate-200" data-testid="trust-block">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-blue-600 text-sm font-semibold uppercase tracking-wide">Before you sign up</p>
            <h2
              className="mt-3 text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              What you get, what you pay, and what happens to your data.
            </h2>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto">
              No fine print, no surprise charges, no marketing spam. Read this first — sign up after.
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            {/* Pricing */}
            <div className="rounded-2xl bg-white border border-slate-200 p-8 shadow-sm" data-testid="trust-pricing">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900">Simple, Transparent Pricing</h3>
              </div>
              <ul className="mt-5 space-y-4 text-slate-700 text-sm leading-relaxed">
                <li>
                  <p className="font-semibold text-slate-900">Try it Free</p>
                  <p className="mt-1">Your first 5 AI item analyses are on the house. See the technology in action before committing.</p>
                </li>
                <li>
                  <p className="font-semibold text-slate-900">Pay Only For What You Need</p>
                  <p className="mt-1">Top up with analysis credit packs starting at just <strong>A$2.99</strong>. No hidden fees, no locked-in subscriptions.</p>
                </li>
                <li>
                  <p className="font-semibold text-emerald-700">Free Inventory Supporting Cover Letter</p>
                  <p className="mt-1">Whenever you need to claim, we generate a covering letter you can send to your insurer along with your inventory — items pre-sorted by settlement pathway with photo evidence attached — to help support a swift claim settlement. Creating, exporting and emailing it is free.</p>
                  <p className="mt-1 text-xs text-slate-500">Rebuilding an inventory <em>after</em> a loss is far more complex. Our in-depth AI Claim Assessment Report for a specific loss event is a separate paid product — <Link to="/pricing" className="text-blue-700 underline underline-offset-2">see pricing</Link>.</p>
                </li>
              </ul>
            </div>

            {/* Privacy */}
            <div className="rounded-2xl bg-white border border-slate-200 p-8 shadow-sm" data-testid="trust-privacy">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center">
                  <Lock className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900">Privacy-First: Your Google Drive</h3>
              </div>
              <p className="mt-4 text-slate-600 text-sm leading-relaxed">
                Your personal data belongs to you. Instead of hoarding your private photos on our servers, VerifAEye links to your Google Drive and stores everything there — on <em>your</em> cloud, not ours.
              </p>
              <ul className="mt-4 space-y-4 text-slate-700 text-sm leading-relaxed">
                <li>
                  <p className="font-semibold text-slate-900">Your Personal Vault</p>
                  <p className="mt-1">Connecting simply lets the app create a secure <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">Verifaeye_Vault</code> folder in your Drive. We don't own your info — you do.</p>
                </li>
                <li>
                  <p className="font-semibold text-slate-900">100% Control</p>
                  <p className="mt-1">Your high-resolution images stay entirely in your personal cloud storage. We process the analysis — your privacy stays locked down, and you retain total ownership.</p>
                </li>
              </ul>
            </div>

            {/* Notifications */}
            <div className="rounded-2xl bg-white border border-slate-200 p-8 shadow-sm" data-testid="trust-notifications">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-50 border border-amber-100 flex items-center justify-center">
                  <BellRing className="w-5 h-5 text-amber-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900">Smart Alerts — Zero Spam</h3>
              </div>
              <p className="mt-4 text-slate-600 text-sm leading-relaxed">
                We won't clog up your inbox with marketing blasts. We only ask for your email and notification permissions so the app can verify your Google Drive ownership and work quietly in the background.
              </p>
              <p className="mt-4 text-slate-700 text-sm font-medium">You'll only be pinged when things happen you actually care about:</p>
              <ul className="mt-2 space-y-2 text-slate-700 text-sm leading-relaxed list-disc list-inside marker:text-slate-400">
                <li>A batch of background photo analyses is complete and ready for review.</li>
                <li>The system detects a potential duplicate item being added.</li>
              </ul>
              <p className="mt-4 text-xs text-slate-500">Plus, you can toggle notifications on or off inside the app any time.</p>
            </div>
          </div>

          <div className="mt-12 text-center">
            {!isAuthenticated && (
              <Link
                to="/register"
                data-testid="trust-block-cta"
                className="inline-block bg-slate-900 text-white px-8 py-3.5 rounded-md font-semibold hover:bg-slate-800 transition"
              >
                Start free — 5 analyses included
              </Link>
            )}
            <p className="mt-3 text-xs text-slate-500">No credit card required to sign up.</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl font-semibold text-slate-900 tracking-tight"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              The proof is in the detail
            </h2>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto">
              When it's time to claim, don't just tell them what you lost — <strong>VerifAEye it.</strong>
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className={`feature-card animate-slide-up stagger-${index + 1}`}
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl font-semibold text-slate-900 tracking-tight"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              How it works
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {[
              { step: '01', title: 'Snap Your Items', desc: 'Take photos of your household contents. Our AI extracts date, location, and device metadata automatically.' },
              { step: '02', title: 'AI Forensic Analysis', desc: 'VerifAEye identifies exact models, assesses condition, and estimates Australian replacement costs.' },
              { step: '03', title: 'You Validate', desc: 'You know your home better than anyone. Review, edit, and confirm the AI\'s findings to create a true reflection of your contents.' },
              { step: '04', title: 'Prove Your Claim', desc: 'Export your verified inventory with receipts and metadata — evidence that\'s impossible to dispute.' }
            ].map((item, index) => (
              <div key={item.step} className={`text-center animate-slide-up stagger-${index + 1}`}>
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-sm sm:text-base">{item.step}</span>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm sm:text-base">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-3xl font-semibold text-white tracking-tight"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          >
            Don't wait until disaster strikes.
          </h2>
          <p className="mt-4 text-white max-w-2xl mx-auto text-lg">
            Start documenting your household contents today with forensic-level detail. 
            When it's time to claim, you'll have the proof — plus a free supporting cover letter to send
            your insurer with your inventory.
          </p>
          <p className="mt-4 text-white/95 max-w-2xl mx-auto text-sm">
            Building your inventory before a loss is simple, cheap and takes minutes. If you leave it until
            after a loss, we can still help you reconstruct and prove your claim — but it's a far more complex
            job and it costs more. Doing it now is the easier, cheaper path.
          </p>
          <div className="mt-8">
            <Link to="/register">
              <Button 
                data-testid="cta-get-started-btn"
                className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 text-base font-semibold"
              >
                Create Your Bionic Inventory
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Guides — direct links from the homepage so these deep pages get crawled and ranked */}
      <section className="bg-slate-50 px-4 py-16 sm:px-6 lg:px-8" data-testid="landing-guides-section">
        <div className="mx-auto max-w-5xl">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600">From the claims desk</p>
          <h2
            className="mt-3 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          >
            What 25 years of insurance claims teaches you
          </h2>
          <p className="mt-3 max-w-2xl text-base text-slate-600">
            Written by{' '}
            <Link to="/authors/bruce-visser" className="font-medium text-blue-600 hover:text-blue-700" data-testid="landing-author-link">
              Bruce Visser
            </Link>
            , who spent a quarter of a century managing contents claims in Australia before building VerifAEye.
          </p>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            <Link
              to="/guides/how-to-make-a-contents-insurance-claim"
              className="group rounded-xl border border-slate-200 bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-lg"
              data-testid="landing-guide-link-claim"
            >
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">Claiming</span>
              <h3 className="mt-2 text-lg font-semibold text-slate-900 group-hover:text-blue-700">
                How to make a contents insurance claim: the 3 things you must prove
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                What your insurer will ask for, what actually counts as proof of ownership, how the value is worked
                out, and the five reasons contents claims get reduced.
              </p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-blue-600">
                Read the guide <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>

            <Link
              to="/guides/tips-on-putting-together-your-contents-inventory"
              className="group rounded-xl border border-slate-200 bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-lg"
              data-testid="landing-guide-link-inventory"
            >
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">Documenting</span>
              <h3 className="mt-2 text-lg font-semibold text-slate-900 group-hover:text-blue-700">
                Tips on putting together your contents inventory
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                The room-by-room method, what to photograph so an assessor can read it, and the things people
                always forget until it is too late.
              </p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-blue-600">
                Read the guide <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          </div>

          <Link
            to="/guides"
            className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-slate-700 hover:text-slate-900"
            data-testid="landing-all-guides-link"
          >
            All guides <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      </main>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-white border-t border-slate-200 pb-24 sm:pb-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <Logo size="sm" />
            <span className="text-slate-600 text-sm ml-2">— The Proof is in the Detail</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <Link to="/guides" className="text-slate-500 hover:text-slate-700 text-sm" data-testid="footer-guides-link">
              Guides
            </Link>
            <Link to="/pricing" className="text-slate-500 hover:text-slate-700 text-sm" data-testid="footer-pricing-link">
              Pricing
            </Link>
            <Link to="/authors/bruce-visser" className="text-slate-500 hover:text-slate-700 text-sm" data-testid="footer-author-link">
              About the author
            </Link>
            <a href="https://symbionic.com.au" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-slate-700 text-sm" data-testid="footer-symbionic-link">
              Symbionic
            </a>
            <Link to="/terms" className="text-slate-500 hover:text-slate-700 text-sm" data-testid="footer-terms-link">
              Terms of Service
            </Link>
            <Link to="/privacy" className="text-slate-500 hover:text-slate-700 text-sm">
              Privacy Policy
            </Link>
            <p className="text-slate-500 text-sm">
              © 2026 VerifAEye. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {!isAuthenticated && <MobileCtaBar />}
    </div>
  );
};

export default Landing;
